// Brigham Young University: public college page (lane 2, #2745), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - BYU's Common Data Set 2025-2026 (Office of Assessment and Planning):
//    C1 applicants, admits and enrollees for Fall 2025 with the residency
//    table, C21/C22 plans, G1/G5 2026-2027 costs. Quirk: the C8 test policy
//    grid has marks in three columns, so the test policy is read from BYU's
//    admissions page instead. C1 gives no total line; the totals are the sums
//    of its rows (5,831 + 6,310 applied, 3,541 + 4,790 admitted, 2,890 +
//    3,853 enrolled), which match the residency table's columns.
//  - BYU Enrollment Services: Application Deadlines (Fall 2027 freshman
//    dates) and ACT/SAT Test Scores (optional for most applicants through
//    winter 2028).
//  - College Scorecard API, school id 230038 (net price by family income).
// Tuition at BYU differs for members of The Church of Jesus Christ of
// Latter-day Saints, which sponsors the university; the CDS lists both, and
// the cost columns here are those two rates, not residency.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'BYU Common Data Set 2025-2026',
        url: 'https://assessmentandplanning.byu.edu/0000019d-e4b0-d088-a1dd-eff755a90000/cds-2025-2026-pdf',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'BYU Enrollment Services, Application Deadlines',
        url: 'https://enrollment.byu.edu/admissions/application-deadlines',
        read: '2026-10-01',
    },
    testing: {
        label: 'BYU Enrollment Services, ACT/SAT Test Scores',
        url: 'https://enrollment.byu.edu/admissions/act-sat-test-scores',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?230038-Brigham-Young-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'brigham-young-university',
    kbId: 'brigham_young_university',
    name: 'Brigham Young University',
    shortName: 'BYU',
    location: 'Provo, UT',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (BYU brand guide); decoration only.
    branding: {
        primary: '#002E5D',
        secondary: '#0047BA',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/brigham_young_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 12141,
        admitted: 8331,
        enrolled: 6743,
        source: 'cds',
        where: 'section C1 (sum of its rows; it gives no total line)',
        residency: [
            { group: 'In-state (Utah)', applied: 4456, admitted: 3194 },
            { group: 'Out-of-state', applied: 7418, admitted: 4973 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'BYU\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'BYU has no early decision or early action plan; the Common Data Set answers no to both. Its November 2 date is a priority deadline, and priority applicants get their decision with everyone else.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application deadline', value: 'December 15' },
        rows: [
            { plan: 'Priority deadline', deadline: 'November 2', decision: 'February 15', binding: false },
            { plan: 'Final deadline', deadline: 'December 15', decision: 'February 15', binding: false },
        ],
        note: 'The application opens August 26, 2026, and every deadline is 11:59 p.m. Mountain Time. All materials must arrive before you can submit. Meeting the priority deadline gives extra consideration in the review but no earlier decision. The scholarship application is due January 2.',
        source: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Test scores are optional for most applicants through winter 2028: usually for freshmen who have completed enough accredited high school coursework. The application tells you whether a score is required; if it is, an official ACT or SAT must be received before you submit. BYU does not superscore, and a score it reviews counts only if it helps your application.',
        source: 'testing',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [
            { topic: 'Testing', line: 'BYU has set its test-optional policy only through winter 2028 so far. It has not said whether students entering in fall 2028 must send ACT or SAT scores.', source: 'testing' },
        ],
        source: 'testing',
    },

    cost: {
        year: '2026-2027',
        columns: ['Church member', 'Not a Church member'],
        rows: [
            { item: 'Tuition', amounts: [7096, 14192] },
            { item: 'Food and housing (on campus)', amounts: [11200, 11200] },
            { item: 'Books and supplies', amounts: [416, 416] },
            { item: 'Transportation', amounts: [2608, 2608] },
            { item: 'Other expenses', amounts: [2088, 2088] },
        ],
        totals: [23408, 30504],
        note: 'For a full-time undergraduate living on campus. BYU charges members of The Church of Jesus Christ of Latter-day Saints a lower tuition; there is no in-state rate. BYU lists no required fees.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 13397,
            byIncome: [
                { income: '$0 to $30,000', amount: 9893 },
                { income: '$30,001 to $48,000', amount: 8507 },
                { income: '$48,001 to $75,000', amount: 9268 },
                { income: '$75,001 to $110,000', amount: 12916 },
                { income: 'Over $110,000', amount: 19162 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from BYU\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
